import * as R from 'ramda';
import React, { useCallback } from 'react';
import { pure, compose, withProps, withState, withHandlers, withPropsOnChange } from 'react-recompose';
// components
import { MuiTextInput } from '../mui-text-input';
// features
import { Input as Input2 } from '../../features/new-do/ui';
import { InputWrapper } from '../../features/new-do/forms/formik/input-wrapper';
// helpers/constants
import * as G from '../../helpers';
import * as GC from '../../constants';
// forms
import { Input } from '../../forms';
// ui
import { Box, Flex } from '../../ui';
/////////////////////////////////////////////////

const enhanceOutsideControl = withPropsOnChange(['outsideValue'], (props) => {
  const { filterValue, outsideValue, setFilterValue } = props;

  if (G.notEquals(filterValue, outsideValue)) {
    setFilterValue(outsideValue);
  }
});

let timeout;

const enhance = compose(
  withProps(() => ({ id: G.genShortId() })),
  withState('typing', 'setTyping', ''),
  withState('filterValue', 'setFilterValue', ''),
  withHandlers({
    handleOnChange: ({
      id,
      noBlur,
      delayTime,
      setTyping,
      handleSetFilter,
      allowSingleCharacter,
      allowSetAdditionalFiltersImmediately,
    }) => (e) => {
      const value = e.target.value;

      clearTimeout(timeout);

      if (allowSetAdditionalFiltersImmediately) {
        handleSetFilter(value);

        return;
      }

      if (R.or(allowSingleCharacter, G.notEquals(value.length, 1))) {
        timeout = setTimeout(() => {
          const el = document.getElementById(id);

          if (R.and(
            R.not(noBlur),
            G.isNotNil(el),
          )) el.blur();

          setTyping(false);
          handleSetFilter(value);
        }, R.or(delayTime, 2000));

        setTyping(true);
      }
    },
  }),
  pure,
);

const onKeyPressHandler = (props, e) => {
  const { value } = e.target;

  const { keyCode, charCode } = e;

  const {
    id,
    setTyping,
    onKeyPressHandler,
    shouldClearTimeoutOnKeyPress,
  } = props;

  const code = R.or(keyCode, charCode);

  if (R.and(R.equals(code, GC.EVENT_KEY_CODE_ENTER), G.isFunction(onKeyPressHandler))) {
    if (shouldClearTimeoutOnKeyPress) clearTimeout(timeout);

    const el = document.getElementById(id);

    if (G.isNotNil(el)) el.blur();

    onKeyPressHandler(value, e);
    setTyping(false);
  }
};

const Component = (props) => (
  <Flex {...R.pathOr({}, ['boxStyles'], props)}>
    {
      R.not(props.withoutLabel) &&
      <Box mr={10}>
        {R.or(props.label, G.getWindowLocale('titles:filter', 'Filter'))}
      </Box>
    }
    <Input
      {...R.pathOr({}, ['inputProps'], props)}
      id={props.id}
      defaultValue={props.filterValue}
      onKeyPress={(e) => onKeyPressHandler(props, e)}
      width={R.pathOr('150px', ['inputProps', 'width'], props)}
      placeholder={R.or(props.placeholder, G.getWindowLocale('titles:filter', 'Filter'))}
      onChange={(e) => {
        props.handleOnChange(e);
        props.setFilterValue(e.target.value);
      }}
    />
    {props.endLabel}
  </Flex>
);

export const Component2 = (props) => {
  const {
    label,
    error,
    errorTop,
    hasError,
    isRequired,
    width = 80,
    endIconName,
    placeholder,
    outsideValue,
    handleOnChange,
    setFilterValue,
    inputWrapperStyles,
  } = props;

  const wrapperProps = {
    width,
    error,
    label,
    errorTop,
    hasError,
    isRequired,
    endIconName,
    inputWrapperStyles,
  };

  return (
    <InputWrapper {...wrapperProps}>
      <Input2
        pl={10}
        pr={18}
        width={width}
        value={outsideValue}
        placeholder={placeholder}
        onKeyPress={(e) => onKeyPressHandler(props, e)}
        onChange={(e) => {
          handleOnChange(e);
          setFilterValue(e.target.value);
        }}
      />
    </InputWrapper>
  );
};

export const Component3 = (props) => {
  const {
    label,
    error,
    errorTop,
    hasError,
    isRequired,
    width = 80,
    placeholder,
    endIconName,
    outsideValue,
    handleSetFilter,
    inputWrapperStyles,
  } = props;

  const wrapperProps = {
    width,
    error,
    label,
    errorTop,
    hasError,
    isRequired,
    endIconName,
    inputWrapperStyles,
  };

  return (
    <InputWrapper {...wrapperProps}>
      <Input2
        pl={10}
        pr={18}
        width={width}
        fontSize={12}
        value={outsideValue}
        placeholder={placeholder}
        onChange={(e) => handleSetFilter(e.target.value)}
      />
    </InputWrapper>
  );
};

export const Component4 = (props) => {
  const {
    type,
    label,
    typing,
    setTyping,
    inputStyles,
    placeholder,
    filterValue,
    useDarkColor,
    handleOnChange,
    setFilterValue,
    handleSetFilter,
  } = props;

  const onBlur = useCallback((e) => {
    const value = G.getEventTargetValue(e);

    if (R.not(typing)) return;

    setTyping(false);
    clearTimeout(timeout);
    handleSetFilter(value);
  }, [typing]);

  return (
    <MuiTextInput
      type={type}
      label={label}
      onBlur={onBlur}
      value={filterValue}
      inputStyles={inputStyles}
      placeholder={placeholder}
      useDarkColor={useDarkColor}
      // data-testid='testid-quick-filter-component-4'
      onKeyPress={(e) => onKeyPressHandler(props, e)}
      onChange={(e) => {
        handleOnChange(e);
        setFilterValue(G.getEventTargetValue(e));
      }}
    />
  );
};

export const QuickFilter = enhance(Component);

export const QuickFilter2 = enhance(Component2);

export const QuickFilterOutsideControl = enhance(enhanceOutsideControl(Component));

export const QuickFilterOutsideControl2 = enhance(enhanceOutsideControl(Component3));

export const QuickFilterOutsideControl3 = enhance(enhanceOutsideControl(Component4));
